import { useState } from 'react'
import { clsx } from 'clsx'
import { clientSigningApi } from '@services/api'
import type { ClientSubmission } from '@/types'
import styles from './ClientSigning.module.css'

interface ClientRiepilogoProps {
  submission: ClientSubmission
  token: string
  onConsentsUpdated: (submission: ClientSubmission) => void
}

export function ClientRiepilogo({ 
  submission, 
  token,
  onConsentsUpdated 
}: ClientRiepilogoProps) {
  const [consensoMarketing, setConsensoMarketing] = useState(submission.consensi.marketing)
  const [consensoComunicazioni, setConsensoComunicazioni] = useState(submission.consensi.comunicazioni)
  const [isSavingConsents, setIsSavingConsents] = useState(false)
  const [consentsChanged, setConsentsChanged] = useState(false)

  const handleConsentChange = async (field: 'marketing' | 'comunicazioni', value: boolean) => {
    if (field === 'marketing') {
      setConsensoMarketing(value)
    } else {
      setConsensoComunicazioni(value)
    }
    setConsentsChanged(true)

    // Auto-save consents
    setIsSavingConsents(true)
    try {
      const updatedData = await clientSigningApi.updateConsents(token, {
        consenso_marketing: field === 'marketing' ? value : consensoMarketing,
        consenso_comunicazioni: field === 'comunicazioni' ? value : consensoComunicazioni
      })
      onConsentsUpdated(updatedData.submission)
    } catch (error) {
      console.error('Error saving consents:', error)
    } finally {
      setIsSavingConsents(false)
      setTimeout(() => setConsentsChanged(false), 2000)
    }
  }

  return (
    <div className={styles.riepilogo}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerIcon}>
          <i className="bi bi-file-earmark-text" />
        </div>
        <div>
          <h1 className={styles.title}>Riepilogo Contratto</h1>
          <p className={styles.subtitle}>
            Verifica i dati e procedi alla firma digitale
          </p>
        </div>
      </div>

      {/* Info Banner */}
      <div className={styles.infoBanner}>
        <i className="bi bi-info-circle" />
        <div>
          <strong>I tuoi dati sono stati pre-compilati</strong>
          <p>Verifica che siano corretti. Puoi modificare solo le preferenze di comunicazione.</p>
        </div>
      </div>

      {/* Dati Anagrafici */}
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <i className="bi bi-person" />
          <span>Dati Anagrafici</span>
        </div>
        <div className={styles.cardGrid}>
          <div className={styles.cardItem}>
            <span className={styles.cardLabel}>Nome e Cognome</span>
            <span className={styles.cardValue}>
              {submission.anagrafica.nome} {submission.anagrafica.cognome}
            </span>
          </div>
          <div className={styles.cardItem}>
            <span className={styles.cardLabel}>Codice Fiscale</span>
            <span className={styles.cardValue}>{submission.anagrafica.codice_fiscale}</span>
          </div>
          <div className={styles.cardItem}>
            <span className={styles.cardLabel}>Telefono</span>
            <span className={styles.cardValue}>{submission.anagrafica.telefono}</span>
          </div>
          <div className={styles.cardItem}>
            <span className={styles.cardLabel}>Email</span>
            <span className={styles.cardValue}>{submission.anagrafica.email}</span>
          </div>
          <div className={clsx(styles.cardItem, styles.fullWidth)}>
            <span className={styles.cardLabel}>Indirizzo di Residenza</span>
            <span className={styles.cardValue}>
              {submission.indirizzo.residenza.indirizzo}, {submission.indirizzo.residenza.cap} {submission.indirizzo.residenza.citta} ({submission.indirizzo.residenza.provincia})
            </span>
          </div>
          {submission.indirizzo.fornitura && !submission.indirizzo.stesso_indirizzo && (
            <div className={clsx(styles.cardItem, styles.fullWidth)}>
              <span className={styles.cardLabel}>Indirizzo di Fornitura</span>
              <span className={styles.cardValue}>
                {submission.indirizzo.fornitura.indirizzo}, {submission.indirizzo.fornitura.cap} {submission.indirizzo.fornitura.citta} ({submission.indirizzo.fornitura.provincia})
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Dati Fornitura */}
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <i className="bi bi-lightning-charge" />
          <span>Fornitura</span>
        </div>
        <div className={styles.cardGrid}>
          <div className={styles.cardItem}>
            <span className={styles.cardLabel}>Tipo</span>
            <span className={styles.cardValue}>
              {submission.tipo_fornitura === 'entrambi' ? 'Luce + Gas' : 
               submission.tipo_fornitura === 'luce' ? 'Solo Luce' : 'Solo Gas'}
            </span>
          </div>
          
          {submission.fornitura.luce && (
            <>
              <div className={styles.cardItem}>
                <span className={styles.cardLabel}>POD</span>
                <span className={styles.cardValue}>{submission.fornitura.luce.pod || '-'}</span>
              </div>
              {submission.fornitura.luce.potenza && (
                <div className={styles.cardItem}>
                  <span className={styles.cardLabel}>Potenza</span>
                  <span className={styles.cardValue}>{submission.fornitura.luce.potenza} kW</span>
                </div>
              )}
            </>
          )}
          
          {submission.fornitura.gas && (
            <>
              <div className={styles.cardItem}>
                <span className={styles.cardLabel}>PDR</span>
                <span className={styles.cardValue}>{submission.fornitura.gas.pdr || '-'}</span>
              </div>
              {submission.fornitura.gas.matricola && (
                <div className={styles.cardItem}>
                  <span className={styles.cardLabel}>Matricola</span>
                  <span className={styles.cardValue}>{submission.fornitura.gas.matricola}</span>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Pagamento */}
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <i className="bi bi-credit-card" />
          <span>Pagamento</span>
        </div>
        <div className={styles.cardGrid}>
          <div className={styles.cardItem}>
            <span className={styles.cardLabel}>Metodo</span>
            <span className={styles.cardValue}>
              {submission.pagamento.metodo === 'iban'
                ? 'Addebito su conto'
                : submission.pagamento.metodo === 'pagoPA'
                  ? 'Bollettino PagoPA'
                  : '—'}
            </span>
          </div>
          {submission.pagamento.metodo === 'iban' && submission.pagamento.iban && (
            <div className={styles.cardItem}>
              <span className={styles.cardLabel}>IBAN</span>
              <span className={styles.cardValue}>{submission.pagamento.iban}</span>
            </div>
          )}
          {submission.pagamento.metodo === 'iban' && submission.pagamento.bic_swift && (
            <div className={styles.cardItem}>
              <span className={styles.cardLabel}>BIC / SWIFT</span>
              <span className={styles.cardValue}>{submission.pagamento.bic_swift}</span>
            </div>
          )}
        </div>
      </div>

      {/* Consensi (modificabili) */}
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <i className="bi bi-shield-check" />
          <span>Consensi e Privacy</span>
          {consentsChanged && (
            <span className={styles.savedBadge}>
              <i className="bi bi-check" /> Salvato
            </span>
          )}
        </div>
        
        {/* Privacy - read only */}
        <div className={styles.consentItem}>
          <div className={styles.consentCheck}>
            <i className={`bi ${submission.consensi.privacy ? 'bi-check-circle-fill' : 'bi-circle'}`} 
               style={{ color: submission.consensi.privacy ? '#16a34a' : '#d1d5db' }} />
          </div>
          <div className={styles.consentContent}>
            <span className={styles.consentLabel}>Informativa Privacy</span>
            <span className={styles.consentDescription}>
              Accetto l'informativa sul trattamento dei dati personali (obbligatorio)
            </span>
          </div>
        </div>

        {/* Marketing - editable */}
        <label className={styles.consentItem}>
          <div className={styles.consentCheck}>
            <input
              type="checkbox"
              checked={consensoMarketing}
              onChange={(e) => handleConsentChange('marketing', e.target.checked)}
              disabled={isSavingConsents}
              className={styles.checkbox}
            />
          </div>
          <div className={styles.consentContent}>
            <span className={styles.consentLabel}>Consenso Marketing</span>
            <span className={styles.consentDescription}>
              Acconsento a ricevere comunicazioni commerciali e promozionali (facoltativo)
            </span>
          </div>
        </label>

        {/* Comunicazioni - editable */}
        <label className={styles.consentItem}>
          <div className={styles.consentCheck}>
            <input
              type="checkbox"
              checked={consensoComunicazioni}
              onChange={(e) => handleConsentChange('comunicazioni', e.target.checked)}
              disabled={isSavingConsents}
              className={styles.checkbox}
            />
          </div>
          <div className={styles.consentContent}>
            <span className={styles.consentLabel}>Comunicazioni Partner</span>
            <span className={styles.consentDescription}>
              Acconsento alla comunicazione dei miei dati a partner commerciali (facoltativo)
            </span>
          </div>
        </label>
      </div>
    </div>
  )
}
